/**
 * Saved views for the Agents console's ticket list (TRUS-687).
 *
 * Built-ins (My tickets, From CS, …) come from the server; if it sends none, the
 * contract's own set is shown. A reviewer can save the current filters as a view,
 * rename it, and delete it. Views are per user on the server
 * (`/agents/rca/views`); params are ids/enums plus the search text.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Bookmark, ChevronDown, Pencil, Trash2, Check, X } from 'lucide-react';
import { tvTokens } from '../../../shared/components/triveny';
import { agentsApi } from '../../../shared/services/platformApi';
import { FALLBACK_BUILTIN_VIEWS, parseRcaViews, type RcaSavedView, type RcaViewParams } from './rcaFilterModel';
import { linkButton, popover, smallButton } from './rcaStyles';

const MAX_NAME = 80;

export function RcaViewsMenu({
  currentParams,
  onApply,
  onMessage,
  isActive,
}: {
  /** The current state as view params (called when saving). */
  currentParams: () => RcaViewParams;
  onApply: (view: RcaSavedView) => void;
  onMessage?: (msg: string) => void;
  /** True for the view the current filters match (its name shows on the button). */
  isActive?: (view: RcaSavedView) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<RcaSavedView[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [busy, setBusy] = useState(false);
  // The views routes refuse (403) anyone without ticket access, while the log answers them
  // with an empty 200. One failed list → no menu: loaded once on mount, never retried.
  const [unavailable, setUnavailable] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const res = await agentsApi.listRcaViews().catch(() => null);
    const parsed = parseRcaViews(res);
    if (parsed === null) {
      setUnavailable(true);
      return;
    }
    setViews(parsed);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const all = views ?? [];
  const serverBuiltins = all.filter((v) => v.builtin);
  const builtins = serverBuiltins.length > 0 ? serverBuiltins : FALLBACK_BUILTIN_VIEWS;
  const mine = all.filter((v) => !v.builtin);
  const active = isActive ? [...builtins, ...mine].find((v) => isActive(v)) ?? null : null;
  const activeViewId = active?.id ?? null;

  const failed = (res: any) => !res || res.success === false;
  if (unavailable) return null;

  const save = async () => {
    const name = newName.trim().slice(0, MAX_NAME);
    if (!name) return;
    setBusy(true);
    try {
      const res: any = await agentsApi.createRcaView({ name, params: currentParams() }).catch(() => null);
      if (failed(res)) {
        onMessage?.(res?.error?.message || 'Could not save the view.');
        return;
      }
      setNewName('');
      setSaving(false);
      onMessage?.(`Saved view "${name}".`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const rename = async (v: RcaSavedView) => {
    const name = renameText.trim().slice(0, MAX_NAME);
    if (!name || name === v.name) {
      setRenaming(null);
      return;
    }
    setBusy(true);
    try {
      const res: any = await agentsApi.updateRcaView(v.id, { name, params: v.params }).catch(() => null);
      if (failed(res)) {
        onMessage?.(res?.error?.message || 'Could not rename the view.');
        return;
      }
      setRenaming(null);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (v: RcaSavedView) => {
    if (typeof window !== 'undefined' && !window.confirm(`Delete the view "${v.name}"?`)) return;
    setBusy(true);
    try {
      const res: any = await agentsApi.deleteRcaView(v.id).catch(() => null);
      if (failed(res)) {
        onMessage?.(res?.error?.message || 'Could not delete the view.');
        return;
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const item: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    padding: '5px 6px',
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: tvTokens.ink700,
    fontSize: 12,
    textAlign: 'left',
    cursor: 'pointer',
  };
  const iconBtn: React.CSSProperties = { display: 'inline-flex', padding: 3, border: 'none', background: 'transparent', color: tvTokens.ink400, cursor: 'pointer', borderRadius: 4 };
  const heading: React.CSSProperties = { padding: '6px 6px 2px', fontFamily: tvTokens.fontMono, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: tvTokens.ink400 };
  const input: React.CSSProperties = { flex: 1, minWidth: 0, height: 26, padding: '0 7px', borderRadius: tvTokens.r, border: `1px solid ${tvTokens.border}`, background: tvTokens.paper, color: tvTokens.ink, fontSize: 12, outline: 'none' };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu" style={smallButton} data-testid="rca-views-button">
        <Bookmark size={11} /> {active ? active.name : 'Views'} <ChevronDown size={12} />
      </button>
      {open && (
        <div style={{ ...popover, minWidth: 260 }} role="menu" aria-label="Saved views">
          <div style={heading}>Built-in</div>
          {builtins.map((v) => (
            <button
              key={v.id}
              type="button"
              role="menuitem"
              style={{ ...item, fontWeight: v.id === activeViewId ? 700 : 500 }}
              onClick={() => {
                onApply(v);
                setOpen(false);
              }}
            >
              {v.name}
            </button>
          ))}
          <div style={heading}>My views</div>
          {views === null && <div style={{ ...item, cursor: 'default', color: tvTokens.ink400 }}>Loading…</div>}
          {views !== null && mine.length === 0 && <div style={{ ...item, cursor: 'default', color: tvTokens.ink400 }}>No saved views yet.</div>}
          {mine.map((v) =>
            renaming === v.id ? (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 2px' }}>
                <input
                  autoFocus
                  aria-label={`Rename ${v.name}`}
                  value={renameText}
                  maxLength={MAX_NAME}
                  onChange={(e) => setRenameText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void rename(v);
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  style={input}
                />
                <button type="button" aria-label="Save the new name" disabled={busy} onClick={() => void rename(v)} style={iconBtn}>
                  <Check size={13} />
                </button>
                <button type="button" aria-label="Cancel rename" onClick={() => setRenaming(null)} style={iconBtn}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  type="button"
                  role="menuitem"
                  style={{ ...item, flex: 1, fontWeight: v.id === activeViewId ? 700 : 500 }}
                  onClick={() => {
                    onApply(v);
                    setOpen(false);
                  }}
                >
                  {v.name}
                </button>
                <button
                  type="button"
                  aria-label={`Rename ${v.name}`}
                  title="Rename"
                  onClick={() => {
                    setRenaming(v.id);
                    setRenameText(v.name);
                  }}
                  style={iconBtn}
                >
                  <Pencil size={12} />
                </button>
                <button type="button" aria-label={`Delete ${v.name}`} title="Delete" disabled={busy} onClick={() => void remove(v)} style={iconBtn}>
                  <Trash2 size={12} />
                </button>
              </div>
            ),
          )}
          <div style={{ borderTop: `1px solid ${tvTokens.borderMuted}`, marginTop: 6, paddingTop: 6 }}>
            {saving ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  autoFocus
                  aria-label="View name"
                  placeholder="View name"
                  value={newName}
                  maxLength={MAX_NAME}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void save();
                    if (e.key === 'Escape') setSaving(false);
                  }}
                  style={input}
                />
                <button type="button" disabled={busy || !newName.trim()} onClick={() => void save()} style={{ ...smallButton, padding: '3px 8px' }}>
                  Save
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setSaving(true)} style={{ ...linkButton, padding: '2px 6px' }}>
                Save current view…
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
